import React, { useState } from 'react'
import { motion } from 'framer-motion'
import {
  SparklesIcon,
  CheckIcon,
  HeartIcon,
  ChatBubbleLeftIcon,
  EyeSlashIcon,
  BoltIcon,
  StarIcon,
  TrophyIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'

const PremiumPage: React.FC = () => {
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly')

  return (
    <div className="space-y-12">
      {/* Hero Section */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r from-primary-600 to-secondary-600 mb-6"
        >
          <SparklesIcon className="h-10 w-10 text-white" />
        </motion.div>
        
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-4xl md:text-6xl font-display font-bold mb-4"
        >
          Unlock Your{' '}
          <span className="gradient-text">Premium Experience</span>
        </motion.h1>
        
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-xl text-white/70 max-w-2xl mx-auto mb-8"
        >
          Get exclusive access to premium features, unlimited connections, and priority support
        </motion.p>

        {/* Billing Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="flex items-center justify-center space-x-4"
        >
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-6 py-3 rounded-xl font-medium transition-all ${
              billingCycle === 'monthly'
                ? 'bg-gradient-to-r from-primary-600 to-secondary-600 text-white'
                : 'bg-white/5 text-white/70 hover:bg-white/10'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle('yearly')}
            className={`px-6 py-3 rounded-xl font-medium transition-all relative ${
              billingCycle === 'yearly'
                ? 'bg-gradient-to-r from-primary-600 to-secondary-600 text-white'
                : 'bg-white/5 text-white/70 hover:bg-white/10'
            }`}
          >
            Yearly
            <span className="absolute -top-2 -right-2 bg-accent-500 text-white text-xs px-2 py-1 rounded-full">
              Save 40%
            </span>
          </button>
        </motion.div>
      </motion.section>

      {/* Pricing Plans */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="grid md:grid-cols-3 gap-8 max-w-7xl mx-auto"
      >
        <PricingCard
          name="Basic"
          price={billingCycle === 'monthly' ? '9.99' : '71.99'}
          period={billingCycle}
          features={[
            'Unlimited Swipes',
            '5 Super Likes per day',
            'See Who Likes You',
            'Advanced Filters',
            'Ad-Free Experience'
          ]}
          popular={false}
        />
        
        <PricingCard
          name="Premium"
          price={billingCycle === 'monthly' ? '19.99' : '143.99'}
          period={billingCycle}
          features={[
            'Everything in Basic',
            'Unlimited Super Likes',
            'Message Read Receipts',
            'Profile Boost (2x/month)',
            'Incognito Mode',
            'Priority Support'
          ]}
          popular={true}
        />
        
        <PricingCard
          name="VIP"
          price={billingCycle === 'monthly' ? '39.99' : '287.99'}
          period={billingCycle}
          features={[
            'Everything in Premium',
            'Unlimited Profile Boosts',
            'Custom Profile Badge',
            'Video Chat Priority',
            'Exclusive VIP Events',
            'Dedicated Account Manager'
          ]}
          popular={false}
        />
      </motion.section>

      {/* Features Grid */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="space-y-6"
      >
        <h2 className="text-3xl font-display font-bold text-center">
          Premium Features
        </h2>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <FeatureCard
            icon={HeartIcon}
            title="Unlimited Likes"
            description="Swipe without limits and connect with anyone"
          />
          <FeatureCard
            icon={ChatBubbleLeftIcon}
            title="Priority Messages"
            description="Your messages get delivered first"
          />
          <FeatureCard
            icon={EyeSlashIcon}
            title="Incognito Mode"
            description="Browse profiles privately"
          />
          <FeatureCard
            icon={BoltIcon}
            title="Profile Boost"
            description="Get 10x more profile views"
          />
          <FeatureCard
            icon={StarIcon}
            title="Super Likes"
            description="Stand out with unlimited super likes"
          />
          <FeatureCard
            icon={TrophyIcon}
            title="VIP Badge"
            description="Premium badge on your profile"
          />
          <FeatureCard
            icon={CheckIcon}
            title="Read Receipts"
            description="See when messages are read"
          />
          <FeatureCard
            icon={SparklesIcon}
            title="Ad-Free"
            description="Enjoy without interruptions"
          />
        </div>
      </motion.section>

      {/* FAQ Section */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="max-w-3xl mx-auto space-y-6"
      >
        <h2 className="text-3xl font-display font-bold text-center mb-8">
          Frequently Asked Questions
        </h2>
        
        <FAQItem
          question="Can I cancel anytime?"
          answer="Yes! You can cancel your subscription at any time. You'll continue to have access until the end of your billing period."
        />
        <FAQItem
          question="What payment methods do you accept?"
          answer="We accept all major credit cards, PayPal, and Apple Pay for your convenience."
        />
        <FAQItem
          question="Is there a free trial?"
          answer="Yes! All new Premium users get a 7-day free trial. Cancel anytime during the trial period."
        />
        <FAQItem
          question="Can I upgrade or downgrade my plan?"
          answer="Absolutely! You can change your subscription tier at any time from your account settings."
        />
      </motion.section>
    </div>
  )
}

interface PricingCardProps {
  name: string
  price: string
  period: 'monthly' | 'yearly'
  features: string[]
  popular: boolean
}

const PricingCard: React.FC<PricingCardProps> = ({ name, price, period, features, popular }) => (
  <motion.div
    whileHover={{ scale: 1.03, y: -8 }}
    className={`card-glass p-8 relative ${
      popular ? 'border-2 border-primary-500' : ''
    }`}
  >
    {popular && (
      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
        <span className="bg-gradient-to-r from-primary-600 to-secondary-600 text-white px-4 py-1 rounded-full text-sm font-medium">
          Most Popular
        </span>
      </div>
    )}
    
    <h3 className="text-2xl font-bold mb-2">{name}</h3>
    <div className="mb-6">
      <span className="text-4xl font-bold">${price}</span>
      <span className="text-white/70">/{period === 'monthly' ? 'mo' : 'yr'}</span>
    </div>
    
    <ul className="space-y-4 mb-8">
      {features.map((feature, index) => (
        <li key={index} className="flex items-start">
          <CheckIcon className="h-5 w-5 text-primary-400 mr-3 flex-shrink-0 mt-0.5" />
          <span className="text-white/80">{feature}</span>
        </li>
      ))}
    </ul>
    
    <motion.button
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      className={popular ? 'btn-primary w-full' : 'btn-glass w-full'}
    >
      Get Started
    </motion.button>
  </motion.div>
)

interface FeatureCardProps {
  icon: React.ComponentType<any>
  title: string
  description: string
}

const FeatureCard: React.FC<FeatureCardProps> = ({ icon: Icon, title, description }) => (
  <motion.div
    whileHover={{ scale: 1.05 }}
    className="card-glass p-6 text-center"
  >
    <Icon className="h-10 w-10 text-primary-400 mx-auto mb-4" />
    <h3 className="font-bold mb-2">{title}</h3>
    <p className="text-sm text-white/70">{description}</p>
  </motion.div>
)

interface FAQItemProps {
  question: string
  answer: string
}

const FAQItem: React.FC<FAQItemProps> = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <motion.div
      className="card-glass overflow-hidden"
      initial={false}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-6 text-left flex items-center justify-between hover:bg-white/5 transition-colors"
      >
        <span className="font-medium text-lg">{question}</span>
        <motion.div
          animate={{ rotate: isOpen ? 45 : 0 }}
          transition={{ duration: 0.2 }}
        >
          {isOpen ? (
            <XMarkIcon className="h-6 w-6 text-primary-400" />
          ) : (
            <span className="text-2xl text-primary-400">+</span>
          )}
        </motion.div>
      </button>
      
      <motion.div
        initial={false}
        animate={{ height: isOpen ? 'auto' : 0 }}
        transition={{ duration: 0.3 }}
        className="overflow-hidden"
      >
        <p className="px-6 pb-6 text-white/70">{answer}</p>
      </motion.div>
    </motion.div>
  )
}

export default PremiumPage
